import Papa from 'papaparse';
import { PropApplicability } from 'BMapsSrc/model/ScopedPropertyGroup';
import { updateLocalJobState } from 'BMapsSrc/redux/jobs/access';
import { deepFreeze } from 'BMapsUtil/js_utils';
import { JobDefinition, JobResultDestinations, JobTypes } from '../JobDefinition';

// Columns produced by rxn_enum's enumerated.csv:
//   reaction_name, direction, reactant_names, reactant_smiles,
//   product_smiles, inchi, inchikey
const scopedPropertyMetadata = deepFreeze({
    compound: {
        childGroups: {
            rxn_enum: {
                metadata: {
                    label: 'Reaction Enumeration',
                    tooltipHeading: 'Reaction enumeration products',
                },
                properties: {
                    product_smiles: {
                        metadata: {
                            label: 'Product',
                            structureValue: true,
                            defaultVisible: true,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    reaction_name: {
                        metadata: {
                            label: 'Reaction',
                            description: 'Named reaction (SMIRKS) that produced this product.',
                            defaultVisible: true,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    reactant_smiles: {
                        metadata: {
                            label: 'Reactants',
                            description: 'Building blocks combined for this product (SMILES).',
                            defaultVisible: true,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    reactant_names: {
                        metadata: {
                            label: 'Reactant Names',
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    inchikey: {
                        metadata: {
                            label: 'InChIKey',
                            defaultVisible: true,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    direction: {
                        metadata: {
                            label: 'Direction',
                            excludeFromTable: true,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    inchi: {
                        metadata: {
                            label: 'InChI',
                            excludeFromTable: true,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                },
            },
        },
    },
});

/**
 * rxn_enum generative job: applies named-reaction SMIRKS to building blocks and
 * parses the enumerated products from the results CSV onto local job state.
 *
 * @augments JobDefinition
 */
export class RxnEnumJob extends JobDefinition {
    static jobTypeId = JobTypes.RXN_ENUM;
    static resultDestination = JobResultDestinations.COMPOUND;
    static jobTypeName = 'Reaction Enumeration';
    static jobName = 'Reaction Enumeration Job';

    static scopedPropertyMetadata = scopedPropertyMetadata;

    /** Result rows are keyed by the product structure. */
    static idField = 'product_smiles';

    constructor(data = {}) {
        super(data);
        this.idField = this.constructor.idField;
    }

    /**
     * Parses the rxn_enum results CSV into structure rows and stores them on
     * local job state (same shape as the REINVENT job).
     * @override
     */
    async integrateResults(job, results) {
        let structures = null;
        if (results.jobResults?.results) {
            structures = Papa.parse(
                results.jobResults.results,
                { header: true, dynamicTyping: true, skipEmptyLines: true }
            ).data
                // Drop rows with no structure (e.g. the trailing blank line) so the
                // workspace loader never builds a load-mol-data spec with no molData.
                .filter((row) => (row?.[this.idField] ?? '').toString().trim())
                // JobsResultsTab loads each compound from a hardcoded `row.SMILES`
                // (the REINVENT contract), ignoring the `structureValue` flag. Our
                // structure column is `product_smiles`, so alias it to `SMILES` or
                // molData comes out empty -> "No molData item in load-mol-data json spec".
                .map((row) => ({ ...row, SMILES: row[this.idField] }));
        }
        updateLocalJobState(job.jobid, { structures });
        return { results };
    }
}
